import { tokens } from '@fluentui/react-components';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import DashboardChartPanel from '@modules/dashboard/components/DashboardChartPanel';
import { StatusLabel } from '@shared/components';
import { TERMINOLOGY } from '@shared/i18n';
import { useRegistrationExpirations } from '../api';
import type { TRegistrationFilters, TRegistrationScopeFilters } from '../types';

type TProps = {
  filters: TRegistrationScopeFilters;
  onChange: (changes: Partial<TRegistrationFilters>) => void;
};

const formatMonth = (month: string) =>
  new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

const RegistrationExpirationsChart = ({ filters, onChange }: TProps) => {
  const { data, error } = useRegistrationExpirations(filters);

  const selectMonth = (month: string) => {
    const lastDay = new Date(`${month}-01T00:00:00Z`);
    lastDay.setUTCMonth(lastDay.getUTCMonth() + 1, 0);
    onChange({
      validUntilFrom: `${month}-01`,
      validUntilTo: lastDay.toISOString().slice(0, 10),
      validity: undefined,
    });
  };

  return (
    <DashboardChartPanel title="Upcoming expirations" caption="Next 6 calendar months">
      <div className="h-44 w-full min-w-0">
        <StatusLabel
          data={data}
          error={error}
          loadingMessage="Loading upcoming expirations..."
          errorMessage="Loading upcoming expirations failed!"
          render={({ months }) => (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={months}
                margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
                className="!cursor-pointer"
                onClick={(state) => {
                  const selected = months.find(({ month }) => month === state?.activeLabel);
                  if (selected) selectMonth(selected.month);
                }}
              >
                <CartesianGrid vertical={false} stroke={tokens.colorNeutralStroke2} strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  tick={({ x, y, payload }) => (
                    <g
                      transform={`translate(${x},${y})`}
                      role="button"
                      tabIndex={0}
                      aria-label={`Filter validity period to ${formatMonth(payload.value)}`}
                      onClick={() => selectMonth(payload.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          event.stopPropagation();
                          selectMonth(payload.value);
                        }
                      }}
                    >
                      <text dy={16} textAnchor="middle" fill={tokens.colorNeutralForeground3} fontSize={12}>
                        {formatMonth(payload.value)}
                      </text>
                    </g>
                  )}
                />
                <YAxis
                  allowDecimals={false}
                  domain={[0, (maximum: number) => Math.max(1, maximum)]}
                  width={36}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: tokens.colorNeutralForeground3, fontSize: 12 }}
                />
                <Tooltip
                  cursor={{ fill: tokens.colorNeutralBackground3 }}
                  labelFormatter={(month) => formatMonth(String(month))}
                  formatter={(value) => [
                    Number(value).toLocaleString(),
                    `Expiring ${TERMINOLOGY.registrationSingle.lower}s`,
                  ]}
                />
                <Bar
                  dataKey="count"
                  fill={tokens.colorBrandForeground1}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        />
      </div>
    </DashboardChartPanel>
  );
};

export default RegistrationExpirationsChart;
